import { useToast } from '@kcd/design-system';

import NonLoginECouponProductPurchaseButton from '~/components/NonLogin/NonLoginECouponProductPurchaseButton';
import { CHECKOUT_ROUTER, VENDORS_ROUTER } from '~/constants/router';
import { useCurrentUser } from '~/domains/auth/queries';
import { useUpdateECoponQuick } from '~/domains/cart/queries';
import ECouponProductPurchaseButton from '~/domains/products/components/ECouponProductPurchaseButton';
import ProductPrice from '~/domains/products/components/ProductPrice';
import ProductImage from '~/domains/products/ProductImage';
import { useTrackProductEventProps } from '~/hooks/event';
import { useLinkComponent } from '~/hooks/mobileApp';
import { usePushWithQuery } from '~/hooks/url';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { UpdateECouponQuickReqParams } from '~/pages/vendors/[vendorId]/products/[offerId]/components/ECoupon/ECouponOrderButton';
import type { OAISchema } from '~/types/openapi';

import PointOnly12Icon from '~/assets/icons/point_only_12x12.svg';

type Props = {
  inverse?: boolean;
  eventOption?: Record<string, unknown>;
  plusMembershipFullBadge?: boolean;
} & OAISchema['MarketSelectProductResponseV2'];

const WideECouponProductCard = ({ eventOption, ...data }: Props) => {
  const { isLoggedIn } = useCurrentUser();
  const LinkComponent = useLinkComponent();
  const {
    offerId,
    vendorId,
    mainImages,
    productName,
    isOutOfStock,
    status,
    inverse,
    eCouponInfo,
    eCouponOrderDisabledLabel,
    eCouponOrderDisabled,
  } = data;
  const toast = useToast();
  const nextPush = usePushWithQuery();
  const [props] = useTrackProductEventProps(data, eventOption);
  const updateQuickECoupon = useUpdateECoponQuick();

  const onClickPurchase = async () => {
    const requestBody: UpdateECouponQuickReqParams = {
      offerId: offerId!,
    };

    try {
      await updateQuickECoupon({
        requestBody,
      });
      void nextPush(CHECKOUT_ROUTER.eCouponRoot, false);
    } catch (error) {
      if (error instanceof OAIApiError) {
        const errMessage = getOAIErrorMessage(error);
        toast(errMessage, {
          offset: 86,
        });
      }
      throw error;
    }
  };
  return (
    <LinkComponent href={VENDORS_ROUTER.productDetail(vendorId, offerId!)}>
      <div
        role="presentation"
        {...props}
        onClick={() => {
          props.onClick?.();
        }}
      >
        <ProductImage
          layout="fill"
          src={mainImages![0]}
          alt={productName}
          isOutOfStock={isOutOfStock}
          status={status}
          inverse={inverse}
          imageWrapperClassName="!aspect-w-[1.75]"
          rightBottom={
            isLoggedIn ? (
              <ECouponProductPurchaseButton
                product={data}
                onClickHandler={onClickPurchase}
                ctaText={eCouponOrderDisabledLabel}
                disabled={eCouponOrderDisabled}
              />
            ) : (
              <NonLoginECouponProductPurchaseButton />
            )
          }
        />
        {eCouponInfo?.eCouponProductType === 'FREE_POINT_ONLY' && (
          <span className="mb-4 flex items-center gap-4 text-blue-600 typo-footnote300-semibold">
            <PointOnly12Icon />
            포인트 전용 상품
          </span>
        )}
        <div className="mb-4 text-hint typo-footnote300-regular">
          {eCouponInfo?.brandName}
        </div>
        <div
          className="mb-4 line-clamp-2 whitespace-pre-line leading-[18px] text-normal"
          style={{ fontSize: '14px' }}
        >
          {productName}
        </div>
        <ProductPrice {...data} reverse />
      </div>
    </LinkComponent>
  );
};

export default WideECouponProductCard;
